import React from "react";
import SectionTitle from "../../components/layouts/others/title-section.layout";
import {Box, Button, Chip, Container, Divider, Link, Paper, Stack, Typography} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBalanceScale,
    faChartLine,
    faChartPie, faDownload,
    faExclamationTriangle, faFilePdf,
    faInfoCircle,
    faLeaf,
    faPiggyBank,
    faPrint,
    faShieldAlt,
    faUniversity
} from "@fortawesome/free-solid-svg-icons";
import Tract3G1 from "../../components/graphs/TRACT-3-G1";
import Tract3G2 from "../../components/graphs/TRACT-3-G2";
import Tract3G3 from "../../components/graphs/TRACT-3-G3";
import Tract3G4 from "../../components/graphs/TRACT-3-G4";
import Tract3Table from "../../components/graphs/TRACT-3-TABLE";
import CandidateHeader from "@others/candidat-header.layout";

const POST20251121: React.FC = () => {

    return (
        <Container maxWidth="lg" sx={{mb: 6}}>
            <SectionTitle title="ESPACE INFORMATIONS DES HABITANTS"/>

            <Box mt={3} mb={3}>
                <Typography
                    variant="h6"
                    fontWeight={700}
                    align="center"
                    gutterBottom
                    color="primary"
                >
                    <FontAwesomeIcon icon={faLeaf} style={{marginRight: 8}}/>
                    VLD2026 - Tract 3 du 21&nbsp;novembre&nbsp;2025
                </Typography>
                <Divider sx={{mt: 1, mb: 3}}/>
            </Box>

            <Stack spacing={4}>
                {/* En-tête */}
                <Box textAlign="center">
                    <CandidateHeader />
                    <Divider sx={{mt: 2, mb: 2}}/>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <FontAwesomeIcon icon={faBalanceScale} color="#1976d2"/>
                        <Typography variant="h6" fontWeight={700} color="primary">
                            Engagement n°1 : Finances
                        </Typography>
                    </Stack>
                    <Typography mt={1} color="text.secondary">
                        Redonner à notre commune une gestion saine et responsable&nbsp;!
                    </Typography>
                </Box>

                {/* Intro philosophie VLD */}
                <Typography align="center" color="text.secondary">
                    Chez <b>VLD</b>, nous souhaitons apporter une information fiable et objective pour
                    enrichir le débat public. Voici une courte présentation du bilan financier
                    de la mandature <b>2018-2025</b>.
                </Typography>

                {/* Constat préoccupant */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                        borderRadius: 2
                    }}
                >
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faExclamationTriangle} color="#c62828" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#c62828">
                                Un constat préoccupant
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            Le bilan financier de la commune laisse apparaître :
                        </Typography>
                        <Stack component="ul" spacing={1.2} sx={{pl: 3}}>
                            <Typography component="li" color="text.secondary">
                                une <b>fuite en avant des dépenses</b>,
                            </Typography>
                            <Typography component="li" color="text.secondary">
                                un <b>affaiblissement des disponibilités financières</b>,
                            </Typography>
                            <Typography component="li" color="text.secondary">
                                un <b>lourd endettement</b>.
                            </Typography>
                        </Stack>

                        {/* (image) – synthèse financière */}
                        <Box
                            mt={2}
                            sx={{
                                borderRadius: 2,
                                border: "2px dashed #c62828",
                                bgcolor: "#ffffff",
                                p: 2,
                                textAlign: "center"
                            }}
                        >
                            <Typography fontWeight={700} color="#c62828" gutterBottom>
                                Synthèse de l’évolution financière 2018-2025
                            </Typography>

                            <Tract3Table/>
                        </Box>

                        <Typography variant="caption" color="text.secondary" mt={2}>
                            M&nbsp;€ = Millions d&apos;euros — Sources : Trésor Public, Compte Financier
                            Unique 2024 (CFU24) &amp; Rapport d&apos;Orientation Budgétaire 2025 (ROB25).
                        </Typography>
                    </Stack>
                </Paper>

                {/* Comment expliquer cette évolution ? */}
                <Paper elevation={2} sx={{p: 3, borderRadius: 2, bgcolor: "#fafafa"}}>
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faInfoCircle} color="#1976d2" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="primary">
                                Comment expliquer cette évolution&nbsp;?
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            Les dépenses de la commune sont composées des <b>charges de
                            fonctionnement</b><sup>1</sup> et des <b>dépenses d&apos;investissement</b><sup>2</sup>.
                        </Typography>
                    </Stack>
                </Paper>

                {/* Charges de fonctionnement */}
                <Paper
                    elevation={3}
                    sx={{p: 3, borderRadius: 2, bgcolor: "#e3f2fd"}}
                >
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faChartPie} color="#1565c0" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#1565c0">
                                Charges de fonctionnement
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            La hausse incontrôlée des <b>charges de fonctionnement</b> a
                            conduit à une lourde <b>augmentation des impôts</b>.
                        </Typography>
                        <Typography color="text.secondary">
                            En sept ans, ces charges ont explosé&nbsp;:
                            <b> +2,5&nbsp;M&nbsp;€</b>, passant de <b>5,0&nbsp;M&nbsp;€ en 2018</b> à
                            <b> 7,5&nbsp;M&nbsp;€ en 2025</b>.
                        </Typography>

                        {/* (image) – évolution charges de fonctionnement */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: "2px dashed #1565c0",
                                bgcolor: "#ffffff",
                                p: 2,
                                textAlign: "center"
                            }}
                        >
                            <Typography fontWeight={700} color="#1565c0" gutterBottom>
                                Évolution des charges de fonctionnement 2018-2025
                            </Typography>

                            <Tract3G1 />
                        </Box>

                        <Typography color="text.secondary">
                            Cette hausse brutale s&apos;est traduite par une
                            augmentation des <b>impôts locaux</b> de <b>2,1&nbsp;M&nbsp;€</b>,
                            passant de <b>3,8&nbsp;M&nbsp;€ en 2018</b> à <b>5,9&nbsp;M&nbsp;€ en 2025</b>.
                        </Typography>

                        <Typography color="text.secondary">
                            L&apos;augmentation des charges, supérieure à celle des recettes, a
                            entraîné une <b>dégradation de la capacité d&apos;autofinancement</b>
                            &nbsp;(sans recours à l&apos;endettement) des projets d&apos;investissement.
                        </Typography>
                    </Stack>
                </Paper>

                {/* Dépenses d'investissement */}
                <Paper
                    elevation={3}
                    sx={{p: 3, borderRadius: 2, bgcolor: "#fff3e0"}}
                >
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faUniversity} color="#ef6c00" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#ef6c00">
                                Dépenses d&apos;investissement
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            Les investissements ont été lancés sans réelle maîtrise&nbsp;:
                            des projets <b>trop coûteux</b> (médiathèque), pour certains
                            <b> non essentiels</b> (cabinets de conseil) ou <b>mal gérés</b>
                            &nbsp;(Pavillon Royal)<sup>3</sup>, d&apos;autres <b>mal priorisés</b> au
                            regard des besoins réels (cour de l&apos;école O. Métra :
                            besoin de protections contre la pluie et le soleil plutôt que
                            des copeaux glissants et des équipements superflus).
                        </Typography>

                        <Typography color="text.secondary">
                            Cette stratégie menée &laquo;&nbsp;à tout prix&nbsp;&raquo; a fortement
                            dégradé la <b>santé financière de la commune</b>.
                        </Typography>

                        <Typography color="text.secondary">
                            La <b>trésorerie</b>, pourtant solide en 2018 (<b>5,7&nbsp;M&nbsp;€</b>)
                            accumulée par les anciennes mandatures, a été réduite à
                            <b> 1,6&nbsp;M&nbsp;€</b> en 2025, soit une <b>perte de 4,1&nbsp;M&nbsp;€</b>.
                        </Typography>

                        {/* (image) – évolution trésorerie */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: "2px dashed #ef6c00",
                                bgcolor: "#ffffff",
                                p: 2,
                                textAlign: "center"
                            }}
                        >
                            <Typography fontWeight={700} color="#ef6c00" gutterBottom>
                                Évolution de la trésorerie 2018-2025
                            </Typography>
                            <Tract3G2 />
                        </Box>
                    </Stack>
                </Paper>

                {/* Encadré définitions */}
                <Paper elevation={1} sx={{p: 2.5, borderRadius: 2, bgcolor: "#f5f5f5"}}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                        Notes&nbsp;:
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" paragraph>
                        (1) Les <b>charges de fonctionnement</b> sont l&apos;ensemble des dépenses
                        nécessaires au fonctionnement des services de la commune.
                        Elles sont notamment constituées des charges de personnel,
                        des achats généraux, des fournitures, des énergies et des
                        subventions versées.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" paragraph>
                        (2) Les <b>dépenses d&apos;investissement</b> concernent le patrimoine
                        de la commune : achats de matériels durables, construction
                        ou aménagement de bâtiments, travaux d&apos;infrastructure.
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                        (3) <b>Pavillon Royal</b> : acquisition sans étude préalable pour y
                        installer le Trait d’Union&nbsp;; le bâtiment s’est finalement révélé
                        inadapté et ne peut pas accueillir l’association de musique.
                    </Typography>
                </Paper>

                {/* Dette */}
                <Paper
                    elevation={3}
                    sx={{p: 3, borderRadius: 2, bgcolor: "#ede7f6"}}
                >
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faPiggyBank} color="#5e35b1" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#5e35b1">
                                Une dette qui explose
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            La <b>dette</b> (vos futurs impôts) a littéralement explosé :
                            <b> +3,6&nbsp;M&nbsp;€</b>. De <b>0,9&nbsp;M&nbsp;€</b> en 2018, elle atteint
                            <b> 4,5&nbsp;M&nbsp;€</b> en 2025.
                        </Typography>

                        {/* (image) – évolution dette */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: "2px dashed #5e35b1",
                                bgcolor: "#ffffff",
                                p: 2,
                                textAlign: "center"
                            }}
                        >
                            <Typography fontWeight={700} color="#5e35b1" gutterBottom>
                                Évolution de la dette 2018-2025
                            </Typography>
                            <Tract3G3/>
                        </Box>
                    </Stack>
                </Paper>

                {/* Pourquoi la situation est-elle préoccupante ? */}
                <Paper
                    elevation={3}
                    sx={{p: 3, borderRadius: 2, background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"}}
                >
                    <Stack spacing={2.5}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faChartLine} color="#c62828" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#c62828">
                                Pourquoi la situation est-elle préoccupante&nbsp;?
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            La hausse des <b>impôts locaux</b> a atteint un niveau
                            fortement asphyxiant pour les Bacots : Bois-le-Roi figure
                            parmi les <b>600 communes françaises de plus de 1&nbsp;000 habitants</b>
                            ayant le <b>taux de taxe foncière le plus élevé</b>.
                        </Typography>

                        <Typography color="text.secondary">
                            Malgré l&apos;augmentation des impôts, la commune n&apos;est
                            désormais plus en capacité de produire des <b>excédents
                            budgétaires</b> permettant de :
                        </Typography>
                        <Stack component="ul" spacing={1.2} sx={{pl: 3}}>
                            <Typography component="li" color="text.secondary">
                                pouvoir <b>entretenir et investir</b> sans avoir recours à la dette,
                            </Typography>
                            <Typography component="li" color="text.secondary">
                                <b>recréer un niveau satisfaisant de trésorerie</b>,
                            </Typography>
                            <Typography component="li" color="text.secondary">
                                <b>amortir les aléas financiers</b>.
                            </Typography>
                        </Stack>

                        <Typography color="text.secondary">
                            En effet, <b>depuis 2020</b>, il n&apos;y a eu <b>aucune création
                            d&apos;excédent</b>.
                        </Typography>

                        {/* (image) – excédents budgétaires */}
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: "2px dashed #c62828",
                                bgcolor: "#ffffff",
                                p: 2,
                                textAlign: "center"
                            }}
                        >
                            <Typography fontWeight={700} color="#c62828" gutterBottom>
                                Excédents / déficits budgétaires depuis 2018
                            </Typography>
                            <Tract3G4 />
                        </Box>

                        <Typography color="text.secondary" fontWeight={700}>
                            Ces résultats ne sont pas une fatalité. Ils sont le signe
                            d&apos;une <b>gestion déséquilibrée</b> et de <b>priorités mal définies</b>.
                        </Typography>
                    </Stack>
                </Paper>

                {/* Nos propositions */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)"
                    }}
                >
                    <Stack spacing={3}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <FontAwesomeIcon icon={faShieldAlt} color="#2e7d32" size="lg"/>
                            <Typography variant="h6" fontWeight={700} color="#2e7d32">
                                Transparence, rigueur et efficacité&nbsp;:
                                nos propositions pour redresser la situation
                            </Typography>
                        </Stack>

                        {/* 1. Transparence */}
                        <Box sx={{bgcolor: "white", borderRadius: 2, p: 2.5}}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                                1. Remettre de la transparence dans les finances
                            </Typography>
                            <Stack component="ul" spacing={1.2} sx={{pl: 3}}>
                                <Typography component="li" color="text.secondary">
                                    Publication annuelle claire du <b>budget</b> et des <b>dépenses</b>.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    <b>Réunion publique</b> de présentation des comptes chaque année.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    <b>Suivi budgétaire</b> accessible en ligne et affiché en mairie.
                                </Typography>
                            </Stack>
                            <Typography mt={1.5} color="text.secondary" fontWeight={700}>
                                Notre objectif&nbsp;: que chacun comprenne où vont les impôts locaux.
                            </Typography>
                        </Box>

                        {/* 2. Équilibre financier */}
                        <Box sx={{bgcolor: "white", borderRadius: 2, p: 2.5}}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                                2. Rétablir l&apos;équilibre financier
                            </Typography>
                            <Stack component="ul" spacing={1.2} sx={{pl: 3}}>
                                <Typography component="li" color="text.secondary">
                                    Réexamen de <b>tous les contrats</b> et des <b>dépenses récurrentes</b>.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    Mise en place d&apos;un <b>plan de désendettement réaliste</b> et suivi
                                    annuel des progrès.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    <b>Mutualisation</b> de certains services avec les communes voisines.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    Engagement&nbsp;: <b>audit des finances</b> et mise en place d&apos;un
                                    <b> plan de réduction des charges de fonctionnement</b>.
                                </Typography>
                            </Stack>
                            <Typography mt={1.5} color="text.secondary" fontWeight={700}>
                                Notre objectif&nbsp;: retrouver une situation financière saine afin
                                de <b>diminuer les impôts fonciers d&apos;ici la fin du mandat</b>.
                            </Typography>
                        </Box>

                        {/* 3. Investir utile */}
                        <Box sx={{bgcolor: "white", borderRadius: 2, p: 2.5}}>
                            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
                                3. Investir utile, ne pas dépenser plus
                            </Typography>
                            <Stack component="ul" spacing={1.2} sx={{pl: 3}}>
                                <Typography component="li" color="text.secondary">
                                    Priorité à <b>l&apos;enfance</b>, au <b>patrimoine</b> (notamment la
                                    sauvegarde de l&apos;église), au <b>sport</b> et à la
                                    <b> transition écologique et énergétique</b>.
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    Recherche active de <b>financements externes</b> (État, Région, etc.).
                                </Typography>
                                <Typography component="li" color="text.secondary">
                                    <b>Entretien et valorisation du patrimoine existant</b> avant de
                                    lancer de nouveaux projets.
                                </Typography>
                            </Stack>
                            <Typography mt={1.5} color="text.secondary" fontWeight={700}>
                                Notre objectif&nbsp;: investir pour l&apos;avenir <b>sans alourdir la dette</b>.
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>

                {/* Conclusion générale */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)"
                    }}
                >
                    <Stack spacing={2}>
                        <Typography variant="h6" fontWeight={700} color="#c62828" align="center">
                            Une commune bien gérée, c&apos;est une commune
                            qui peut investir, innover et soutenir ses habitants.
                        </Typography>
                        <Typography align="center" color="text.secondary">
                            Faisons de la gestion communale un <b>levier d&apos;avenir</b>&nbsp;!
                        </Typography>
                    </Stack>
                </Paper>

                <Box
                    sx={{
                        mt: 5,
                        mb: 5,
                        bgcolor: "#f7f3ee",
                        borderRadius: 3,
                        p: 4,
                        boxShadow: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <FontAwesomeIcon icon={faFilePdf} size="2x" style={{ color: "#d32f2f" }} />
                        <Typography variant="h6" fontWeight={700} color="primary">
                            Télécharger le tract au format PDF
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" mb={2} align="center">
                        Retrouvez toutes les informations de l'article sous forme de tract à imprimer ou partager !
                    </Typography>
                    <a
                        href="/files/TRACT-VLD2026-3-2025-11-21.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            textDecoration: "none",
                        }}
                    >
                        <Chip
                            icon={<FontAwesomeIcon icon={faDownload} style={{ color: "#fff" }} />}
                            label="Télécharger le PDF"
                            sx={{
                                bgcolor: "#d32f2f",
                                color: "#fff",
                                px: 3,
                                py: 2,
                                fontWeight: 600,
                                fontSize: "1rem",
                                "&:hover": {
                                    bgcolor: "#b71c1c",
                                },
                            }}
                            clickable
                        />
                    </a>
                </Box>

                <Typography
                    align="center"
                    variant="subtitle2"
                    sx={{fontStyle: "italic", mt: 3, fontWeight: 600}}
                >
                    Votre équipe VLD2026
                </Typography>
            </Stack>
        </Container>
    );
};

export default POST20251121;